import React, { useCallback, useEffect, useRef } from 'react';
import { Modal as RsModal } from 'rsuite';
import { AlertTriangle, CheckCircle2, HelpCircle } from 'lucide-react';
import { Button } from '../../atoms/button';
import { BUTTON_LABELS } from '../../constants/strings';
import styles from './ConfirmModal.module.scss';

export type ConfirmVariant = 'danger' | 'warning' | 'primary' | 'success';

export interface ConfirmModalProps {
  /** Controls visibility. */
  isOpen: boolean;
  /** Dismiss — fired by Cancel, Escape and the backdrop. */
  onClose: () => void;
  /** Optional dismiss hook when Cancel specifically needs different handling from a close. */
  onCancel?: () => void;
  /** Runs the confirmed action. May be async; keep `isLoading` in sync with it. */
  onConfirm: () => void;
  title: string;
  /** The question being asked. A node is allowed so a caller can emphasise a name. */
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Drives the icon and the confirm button colour. Destructive actions use `danger`. */
  variant?: ConfirmVariant;
  /** Blocks a second submission and shows the spinner while the action is in flight. */
  isLoading?: boolean;
  /** Greys out Confirm for callers that gate on their own precondition. */
  confirmDisabled?: boolean;
  /** Overrides the variant's default glyph. */
  icon?: React.ReactNode;
}

const variantIconClass: Record<ConfirmVariant, string> = {
  danger: styles.iconDanger,
  warning: styles.iconWarning,
  primary: styles.iconPrimary,
  success: styles.iconSuccess,
};

function defaultIcon(variant: ConfirmVariant) {
  if (variant === 'danger' || variant === 'warning') return <AlertTriangle size={28} />;
  if (variant === 'success') return <CheckCircle2 size={28} />;
  return <HelpCircle size={28} />;
}

/**
 * The single confirmation dialog for the app — log out, delete, remove, deactivate, close, and
 * every other irreversible action. Anything that only needs "are you sure?" belongs here rather
 * than in a hand-rolled modal, so the whole product asks the question the same way.
 *
 * One centred column — icon, title, question, actions — sitting in the middle of the viewport.
 * There is no close X: a confirm holds no input to lose, so Cancel, Escape and the backdrop are
 * dismissal enough, and dropping it keeps the column symmetrical.
 *
 * Destructive intent is carried by the icon disc and the confirm button; Cancel stays a plain
 * secondary button and takes initial focus on a `danger` dialog so a stray Enter cannot destroy
 * anything. While `isLoading` is set every dismissal path is closed off, which is what stops a
 * double submission.
 */
export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onCancel,
  onConfirm,
  title,
  message,
  confirmLabel = BUTTON_LABELS.CONFIRM,
  cancelLabel = BUTTON_LABELS.CANCEL,
  variant = 'primary',
  isLoading = false,
  confirmDisabled = false,
  icon,
}) => {
  const titleId = React.useId();
  const messageId = React.useId();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);

  /* An in-flight action owns the dialog: Escape and the backdrop go quiet until it settles, so
     the caller never has to guard against a close mid-request. */
  const handleClose = useCallback(() => {
    if (isLoading) return;
    onClose();
  }, [isLoading, onClose]);

  const handleCancel = useCallback(() => {
    if (isLoading) return;
    (onCancel ?? onClose)();
  }, [isLoading, onCancel, onClose]);

  /* Park focus on the safer of the two actions once the dialog has painted. */
  useEffect(() => {
    if (!isOpen) return;

    const target = variant === 'danger' ? cancelRef : confirmRef;
    const frame = window.requestAnimationFrame(() => target.current?.focus());

    return () => window.cancelAnimationFrame(frame);
  }, [isOpen, variant]);

  return (
    <RsModal
      open={isOpen}
      onClose={handleClose}
      size="xs"
      centered
      backdrop={isLoading ? 'static' : true}
      keyboard={!isLoading}
      role="alertdialog"
      aria-labelledby={titleId}
      aria-describedby={messageId}
      className={styles.dialog}
    >
      <RsModal.Body>
        <div className={styles.body}>
          <div className={`${styles.icon} ${variantIconClass[variant]}`} aria-hidden="true">
            {icon ?? defaultIcon(variant)}
          </div>

          <h2 id={titleId} className={styles.title}>
            {title}
          </h2>
          <div id={messageId} className={styles.message}>
            {message}
          </div>

          <div className={styles.actions}>
            <Button ref={cancelRef} variant="secondary" onClick={handleCancel} disabled={isLoading}>
              {cancelLabel}
            </Button>
            <Button
              ref={confirmRef}
              variant={variant === 'warning' ? 'danger' : variant}
              onClick={onConfirm}
              isLoading={isLoading}
              disabled={isLoading || confirmDisabled}
            >
              {confirmLabel}
            </Button>
          </div>
        </div>
      </RsModal.Body>
    </RsModal>
  );
};

export default ConfirmModal;
